import React, { useRef, useState, useEffect } from 'react';
import { useAppSelector } from '../../../contexts/hooks';
import Form, { GroupItem, Item } from 'devextreme-react/form';
import { CustomFormControl } from '../../../components/utils/form-render/FormRender';
import { Button, SelectBox, TextBox, DateRangeBox, Popup } from 'devextreme-react';
import { useApiOrders } from '../../../api/api.orders';
import { newToast } from '../../../components';
import { useScreenSize } from '../../../utils/media-query';
import { ToolbarItem } from 'devextreme-react/popup';
import { ConvertLocalDate } from '../../../utils/functions';
import { CustomFilters, TypeTableFilter } from '../../../types/types.system';
import { shallowEqual } from 'react-redux';

const fieldsExclude = ['Pc_Foto', 'Ord_Se_Id', 'Pc_Gn_Id', 'Ord_FechaCreacion', 'Pc_Ti_Id'];

export const FilterComponent = ({ onApplyFilters, visible, setVisible, includeOrderFields = true, includePatientFields = true }: { onApplyFilters: (filters) => void; visible: boolean; setVisible: (state) => void; includeOrderFields?: boolean; includePatientFields?: boolean }) => {
  const info = useAppSelector((x) => x.configuration.Data?.SystemFields)
    ?.filter((x) => ((includeOrderFields && x.Dfc_Tabla === 'Ordenes') || (includePatientFields && x.Dfc_Tabla === 'Pacientes')) && x.Dfc_Estado === true && x.Dfc_Visible === true)
    .sort((a, b) => a.Dfc_Orden - b.Dfc_Orden);
  const lstServicios = useAppSelector((x) => x.configuration.Data?.ComboBox?.find((x) => x.Name === 'SysA_Servicios')?.Data) || [];
  const formRef = useRef<Form>(null);
  const dateRangeRef = useRef<DateRangeBox>(null);
  const identificacionRef = useRef<TextBox>(null);
  // Estado para almacenar los valores de los filtros
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const { checkExistentOrderByConsec } = useApiOrders();
  const [loadingConsec, setLoadingConsec] = useState(false);
  const [orderInput, setOrderInput] = useState('');
  const { isXSmall, isSmall } = useScreenSize();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const LstSedes = useAppSelector((x) => x.configuration.Data?.ComboBox)?.find((x) => x.Name === 'SysI_Sedes')?.Data;
  const Prm_Ord_ViewOtherLocation = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_ViewOtherLocation);
  const ModuleLocation: boolean = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'ModuleLocation')[0].Cgn_Resultado, shallowEqual) || 'false');

  // Función para establecer el foco en el primer campo visible
  const setInitialFocus = () => {
    if (includeOrderFields && dateRangeRef.current) {
      // Si el campo de fecha está visible, hacer focus ahí
      dateRangeRef.current.instance.focus();
    } else if (identificacionRef.current) {
      // Si no, hacer focus en identificación
      identificacionRef.current.instance.focus();
    }
  };

  const setData = (field, castMode) => (value) => {
    let resp = value;
    switch (castMode) {
      case 'LOWER':
        resp = value.toString().toLocaleLowerCase();
        break;
      case 'UPPER':
        resp = value.toString().toUpperCase();
        break;
    }
    if (field.includes('AditionalFields')) {
      const key = field.split('.')[1];
      setFilters((prev) => ({
        ...prev,
        AditionalFields: {
          ...(prev.AditionalFields || {}),
          [key]: resp,
        },
      }));
      return;
    }
    setFilters((prev) => ({
      ...prev,
      [field]: resp,
    }));
  };

  const findOrder = (value) => {
    setLoadingConsec(true);
    checkExistentOrderByConsec(value.value)
      .then((res) => {
        if (!res) {
          return;
        }
        if (res.Consec) {
          setOrderInput(res.Consec);
          setFilters((prev) => ({
            ...prev,
            Ord_Numero: res.Consec,
          }));
        } else if (res.Error) {
          newToast('WARNING', res.Error, 4);
          setOrderInput('');
          setFilters((prev) => ({
            ...prev,
            Ord_Numero: '',
          }));
        }
      })
      .finally(() => {
        setLoadingConsec(false);
      });
  };

  const handleOrderInputChange = (value) => {
    setOrderInput(value);
  };

  const handleOrderInputBlur = () => {
    findOrder({ value: orderInput });
  };

  // Generar los filtros a partir de los valores ingresados
  const generateFilters = () => {
    const filterList: CustomFilters[] = [];

    // 1. Filtro para número de orden (igual)
    if (filters['Ord_Numero']) {
      filterList.push({
        Field: 'Ord_Numero',
        TypeFilter: TypeTableFilter.Equal,
        Value: filters['Ord_Numero'],
      });
    }

    // 2. Filtro para identificación (contains)
    if (filters['Pc_Identificacion']) {
      filterList.push({
        Field: 'Pc_Identificacion',
        TypeFilter: TypeTableFilter.Contains,
        Value: filters['Pc_Identificacion'],
      });
    }

    // 3. Filtro para fecha de creación (entre)
    if (dateRange[0] && dateRange[1]) {
      filterList.push({
        Field: 'Ord_FechaCreacion',
        TypeFilter: TypeTableFilter.Between,
        Value: ConvertLocalDate(dateRange[0]),
        Value2: ConvertLocalDate(dateRange[1]),
      });
    }

    // 3. Filtro para servicio (igual)
    if (filters['Ord_Se_Id']) {
      filterList.push({
        Field: 'Ord_Se_Id',
        TypeFilter: TypeTableFilter.Equal,
        Value: filters['Ord_Se_Id'],
      });
    }

    // 3. Filtro para sede (igual)
    if (filters['Ord_Sd_Id']) {
      filterList.push({
        Field: 'Ord_Sd_Id',
        TypeFilter: TypeTableFilter.Equal,
        Value: filters['Ord_Sd_Id'],
      });
    }

    // 4. Agregar filtros para campos "normales" (fuera de AditionalFields)
    info?.forEach((field) => {
      const fieldValue = filters[field.Dfc_Equivalencia];

      // Solo agregar el filtro si el campo tiene un valor y no está excluido
      if (fieldValue && !fieldsExclude.includes(field.Dfc_Equivalencia)) {
        // Determinar el tipo de filtro (Equal o Contains)
        let typeFilter: TypeTableFilter = TypeTableFilter.Contains; // Por defecto es Contains

        // Si el campo es de tipo select, lookup, datebox, o numberbox, usar Equal
        const fieldType = field.TipoCampo?.Tcm_Equivalencia;
        if (['dxSelectBox', 'dxLookup', 'dxDateBox', 'dxNumberBox'].includes(fieldType)) {
          typeFilter = TypeTableFilter.Equal;
        }

        // Agregar el filtro a la lista
        filterList.push({
          Field: field.Dfc_Equivalencia,
          TypeFilter: typeFilter,
          Value: fieldValue,
        });
      }
    });

    // 5. Agregar filtros para campos dentro de "AditionalFields"
    if (filters['AditionalFields']) {
      const aditionalFields = filters['AditionalFields'];

      // Iterar sobre cada campo dentro de AditionalFields
      Object.keys(aditionalFields).forEach((key) => {
        const fieldValue = aditionalFields[key];

        // Excluir campos con valor null o undefined
        if (fieldValue != null) {
          filterList.push({
            Field: key,
            TypeFilter: TypeTableFilter.Equal,
            Value: fieldValue,
          });
        }
      });
    }

    if (filterList.length === 0) {
      newToast('WARNING', 'No se han aplicador filtros', 4);
      return;
    }

    onApplyFilters(filterList);
  };

  const handleCancel = () => {
    setFilters({});
    setDateRange([null, null]);
    setOrderInput('');
    setVisible(false);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 2. Acceda a la instancia de DevExtreme Form a través de la referencia
    const formInstance = formRef.current?.instance;

    // Si la validación de DevExtreme es exitosa, ejecute la función
    if (formInstance) {
      const validationResult = formInstance.validate();
      if (validationResult.isValid) {
        generateFilters();
      }
    }
  };

  return (
    <Popup height='auto' title='Filtrar' showCloseButton={true} onHidden={handleCancel} onShown={setInitialFocus} fullScreen={isXSmall || isSmall} hideOnOutsideClick={true} width={500} visible={visible}>
      <form onSubmit={handleFormSubmit}>
        <Form className='plain-styled-form' ref={formRef}>
          <GroupItem colCount={2}>
            {/* Filtro de Rango de Fechas */}
            <Item colSpan={2} visible={includeOrderFields}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ marginBottom: 4 }}>Rango de Fechas</label>
                <DateRangeBox ref={dateRangeRef} value={dateRange} onValueChange={(value) => setDateRange(value as [Date | null, Date | null])} />
              </div>
            </Item>

            <Item colSpan={1}>
              <TextBox ref={identificacionRef} label='Identificación' stylingMode='outlined' labelMode='static' value={filters['Pc_Identificacion'] || ''} validationError={null} onValueChange={setData('Pc_Identificacion', 'UPPER')} />
            </Item>
            {includeOrderFields && (
              <Item colSpan={1}>
                <TextBox label='Orden' stylingMode='outlined' labelMode='static' disabled={loadingConsec} value={orderInput} validationError={null} onValueChange={handleOrderInputChange} onFocusOut={handleOrderInputBlur} />
              </Item>
            )}

            {includeOrderFields && (
              <Item colSpan={1}>
                <SelectBox label='Sede' stylingMode='outlined' labelMode='static' value={filters['Ord_Sd_Id'] || ''} searchEnabled={true} validationError={null} items={LstSedes} displayExpr='Sd_Nombre' valueExpr='Sd_Id' onValueChange={setData('Ord_Sd_Id', null)} />
              </Item>
            )}

            {info && info.filter((x) => x.Dfc_Equivalencia === 'Ord_Se_Id').length > 0 && (
              <Item colSpan={1}>
                <SelectBox label='Servicio' stylingMode='outlined' labelMode='static' value={filters['Ord_Se_Id'] || ''} searchEnabled={true} validationError={null} items={lstServicios} displayExpr='Se_Nombre' valueExpr='Se_Id' onValueChange={setData('Ord_Se_Id', null)} />
              </Item>
            )}

            {info
              ?.filter((x) => !fieldsExclude.includes(x.Dfc_Equivalencia) && x.Dfc_Tcm_Id !== 3 && x.Dfc_Tcm_Id !== 8 && x.Dfc_Tcm_Id !== 6)
              .map((field) => {
                const currentDep = info.find((y) => field.Dfc_DependencyDefinition === y.Dfc_Id)?.Dfc_Equivalencia;
                return (
                  <Item key={field.Dfc_Id} colSpan={1}>
                    <CustomFormControl valueDepency={field.Dfc_Dependency && currentDep && filters.AditionalFields ? filters.AditionalFields[currentDep] : undefined} value={field.Dfc_Dinamico && filters.AditionalFields ? filters.AditionalFields[field.Dfc_Equivalencia] || null : filters[field.Dfc_Equivalencia]} data={field} onValueChange={setData(field.Dfc_Dinamico ? `AditionalFields.${field.Dfc_Equivalencia}` : field.Dfc_Equivalencia, field.Dfc_Cast)} isEditing={true} />
                  </Item>
                );
              })}
          </GroupItem>
        </Form>
        <button type='submit' style={{ display: 'none' }} aria-hidden />
      </form>

      <ToolbarItem toolbar='bottom' location='after'>
        <Button text='Cancelar' stylingMode='contained' onClick={handleCancel} className='cancel-button' />
        <Button text={'Confirmar'} disabled={loadingConsec} stylingMode='contained' type='default' onClick={generateFilters} />
      </ToolbarItem>
    </Popup>
  );
};
